import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { RiskBadge, RiskIndicator } from "@/components/RiskBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Search, 
  Building2, 
  Loader2, 
  AlertTriangle, 
  FileText,
  Shield,
  Globe
} from "lucide-react";
import { toast } from "sonner";

const CompanySearchPage = () => {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState("");
  const [country, setCountry] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [savingReport, setSavingReport] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!companyName) {
      toast.error("Please enter a company name");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const res = await axios.post(`${API}/companies/screen`, {
        company_name: companyName,
        country: country || null,
      });
      setResult(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Screening failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReport = async () => {
    if (!result) {
      toast.error("No screening results to save");
      return;
    }

    setSavingReport(true);
    try {
      const reportData = {
        vessel_screening: result,
        overall_risk: result.risk_level,
        recommendations: getRecommendations(result.risk_level),
        data_sources_consulted: result.screened_sources || [],
        source_statuses: result.source_statuses || [],
        notes: `Company screening for: ${companyName}`,
      };

      const res = await axios.post(`${API}/reports`, reportData);
      toast.success("Report saved successfully");
      navigate(`/reports/${res.data.id}`);
    } catch (error) {
      toast.error("Failed to save report");
    } finally {
      setSavingReport(false);
    }
  };

  const getRecommendations = (riskLevel) => {
    if (riskLevel === "GREEN") {
      return ["No sanctions matches found. Proceed with standard due diligence."];
    } else if (riskLevel === "YELLOW") {
      return [
        "Potential match found - additional verification required.",
        "Contact compliance team for further review.",
        "Request additional documentation from counterparty."
      ];
    } else {
      return [
        "HIGH RISK - Do not proceed without compliance approval.",
        "Escalate to senior compliance officer immediately.",
        "Document all findings and decisions."
      ];
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="company-search-page">
        {/* Search Card */}
        <Card>
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg font-serif text-[#0b3e65] flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Company Sanctions Screening
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="companyName">Company Name *</Label>
                  <Input
                    id="companyName"
                    placeholder="e.g., LASKARIDIS SHIPPING CO LTD"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="rounded-sm"
                    data-testid="input-company-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country (Optional)</Label>
                  <Input
                    id="country"
                    placeholder="e.g., Greece, Russia, Iran"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="rounded-sm"
                    data-testid="input-country"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  type="submit"
                  disabled={loading || !companyName}
                  className="bg-[#0b3e65] hover:bg-[#0b3e65]/90 rounded-sm"
                  data-testid="btn-screen-company"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                  <span className="ml-2">Screen Company</span>
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Screens company name against OFAC SDN, UN Security Council, and EU Consolidated sanctions lists
              </p>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {result && (
          <div className="space-y-6">
            {/* Risk Summary */}
            <RiskIndicator
              risk={result.risk_level}
              title={`Sanctions Screening Result: ${result.risk_level === "GREEN" ? "Clear" : result.risk_level === "YELLOW" ? "Potential Match" : "High Risk Match"}`}
              description={result.notes}
            />

            {/* Screening Details */}
            <Card>
              <CardHeader className="border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-serif text-[#0b3e65]">Screening Details</CardTitle>
                  <RiskBadge risk={result.risk_level} />
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="p-4 bg-slate-50 rounded-sm">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Screened Entity</p>
                    <p className="font-semibold text-[#0b3e65] flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      {result.entity_name}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-sm">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Screening Date</p>
                    <p className="font-medium">
                      {new Date(result.screening_date).toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="mb-6">
                  <p className="text-sm text-slate-600 mb-2">
                    <strong>Screened Against:</strong>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {result.screened_sources?.map((source, idx) => (
                      <span key={idx} className="px-2 py-1 bg-[#0b3e65]/10 text-[#0b3e65] text-xs rounded-sm font-medium">
                        {source}
                      </span>
                    ))}
                  </div>
                </div>

                {result.sanctions_matches?.length > 0 ? (
                  <div>
                    <h4 className="font-semibold text-red-600 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Potential Matches Found ({result.sanctions_matches.length})
                    </h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Source</TableHead>
                          <TableHead>Matched Name</TableHead>
                          <TableHead>Match Score</TableHead>
                          <TableHead>Program</TableHead>
                          <TableHead>Reason</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {result.sanctions_matches.map((match, idx) => (
                          <TableRow key={idx} className="hover:bg-red-50/50">
                            <TableCell className="font-medium">{match.source}</TableCell>
                            <TableCell>{match.entity_name}</TableCell>
                            <TableCell>
                              <span className={`font-mono font-semibold ${match.match_score >= 95 ? "text-red-600" : match.match_score >= 85 ? "text-amber-600" : "text-slate-600"}`}>
                                {match.match_score.toFixed(1)}%
                              </span>
                            </TableCell>
                            <TableCell>{match.program || "N/A"}</TableCell>
                            <TableCell className="max-w-xs truncate">{match.reason || "N/A"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 bg-emerald-50 rounded-sm border border-emerald-100">
                    <Shield className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                    <p className="text-emerald-700 font-semibold">No sanctions matches found</p>
                    <p className="text-sm text-emerald-600 mt-1">
                      Company cleared against OFAC SDN, UN Security Council, and EU Consolidated lists
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <Button
                onClick={handleSaveReport}
                disabled={savingReport}
                className="bg-[#0b3e65] hover:bg-[#0b3e65]/90 rounded-sm"
                data-testid="btn-save-company-report"
              >
                {savingReport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Save Compliance Report
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CompanySearchPage;
